/**
 * Named gesture-tuning presets.
 *
 * Centralising the recognizer's `fling`/`thresholdPx` together with the
 * consumer-facing layout constants (commit thresholds, rubber-band limits,
 * animation durations) keeps the feel consistent across every swipeable surface
 * and makes tuning a one-line change.
 */

import type { FlingConfig } from "./types";

const GESTURE_DEADZONE_PX = 8;

const STANDARD_FLING: FlingConfig = {
  maxDurationMs: 300,
  minDistancePx: 50,
  axisRatio: 2,
};

/** Per-row swipe-to-action for operational lists (products, counts, …). */
export const STOCK_ROW_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 96,
  rubberBandLimitPx: 128,
  rubberBandFactor: 0.25,
  hapticMs: 15,
  commitAnimationMs: 280,
  cancelAnimationMs: 220,
  fallbackWidthPx: 400,
} as const;

/**
 * Swipe-to-dismiss for a transient toast. Horizontal single axis so it never
 * fights vertical page scroll behind it. Dismissable in either direction; the
 * card follows the finger and commits past a fraction of its own width (or on a
 * fling), otherwise snaps back to center.
 */
export const TOAST_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 80,
  commitThresholdRatio: 0.4,
  rubberBandLimitPx: 9999,
  rubberBandFactor: 1,
  hapticMs: 12,
  commitAnimationMs: 220,
  cancelAnimationMs: 220,
  fallbackWidthPx: 400,
  fling: { ...STANDARD_FLING },
} as const;
